import Page from "@/components/layout/Page";
import { SEOHead } from "@/components/seo/SEOHead";
import { BreadcrumbSchema } from "@/components/seo/BreadcrumbSchema";

export default function TermsOfService() {
  return (
    <Page>
      <SEOHead
        title="Terms of Service"
        description="Samboards terms of service - the terms and conditions governing your use of our website and purchase of our sensory products."
        canonical="/terms-of-service"
      />
      <BreadcrumbSchema
        items={[
          { name: "Home", url: "/" },
          { name: "Terms of Service", url: "/terms-of-service" }
        ]}
      />

      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-teal-400">
            Terms of Service
          </h1>
          <p className="text-slate-400 mb-12">Last updated: November 2025</p>

          <div className="space-y-8 text-slate-300">
            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">1. Agreement to Terms</h2>
              <p className="leading-relaxed">
                By accessing or using the Samboards website and services, you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any of these terms, you are prohibited from using this site.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">2. Use of Website</h2>
              <h3 className="text-xl font-medium text-slate-200 mb-3 mt-4">Permitted Use</h3>
              <p className="leading-relaxed mb-3">
                You may use our website for lawful purposes only. You agree not to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Transmit harmful or malicious code</li>
                <li>Attempt to gain unauthorized access to our systems</li>
                <li>Use automated systems to scrape or collect data</li>
                <li>Impersonate another person or entity</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">3. Product Information</h2>
              <p className="leading-relaxed">
                We strive to provide accurate product descriptions and pricing. However, we do not warrant that product descriptions, pricing, or other content is accurate, complete, reliable, current, or error-free. We reserve the right to correct any errors and to change or update information at any time without prior notice.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">4. Orders and Payment</h2>
              <h3 className="text-xl font-medium text-slate-200 mb-3 mt-4">Order Acceptance</h3>
              <p className="leading-relaxed mb-3">
                All orders are subject to acceptance and availability. We reserve the right to refuse or cancel any order for any reason, including:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Product availability</li>
                <li>Errors in product or pricing information</li>
                <li>Suspected fraudulent or unauthorized transactions</li>
              </ul>

              <h3 className="text-xl font-medium text-slate-200 mb-3 mt-6">Payment</h3>
              <p className="leading-relaxed">
                Payment is processed securely through Stripe. By providing payment information, you represent that you are authorized to use the payment method. All prices are in GBP (£) and include VAT where applicable.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">5. Shipping and Delivery</h2>
              <p className="leading-relaxed mb-3">
                We aim to process and ship orders promptly. Delivery times are estimates and not guaranteed. Risk of loss and title for products pass to you upon delivery to the carrier.
              </p>
              <p className="leading-relaxed">
                Please inspect products upon delivery and notify us of any damage or defects within 7 days.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">6. Returns and Refunds</h2>
              <p className="leading-relaxed mb-3">
                Under UK Consumer Rights Act 2015, you have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Cancel your order within 14 days of receipt</li>
                <li>Return faulty products for repair, replacement, or refund</li>
                <li>Request a refund if products are not as described</li>
              </ul>
              <p className="leading-relaxed mt-3">
                To initiate a return, please contact us at samboards19@aol.com. Products must be returned in their original condition with all packaging and documentation.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">7. Intellectual Property</h2>
              <p className="leading-relaxed">
                All content on this website, including text, graphics, logos, images, and software, is the property of Samboards and protected by UK and international copyright laws. You may not reproduce, distribute, or create derivative works without our written permission.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">8. Product Liability and Disclaimer</h2>
              <p className="leading-relaxed mb-3">
                Our sensory products are designed to provide tactile stimulation and support. However:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Products should be used under appropriate supervision</li>
                <li>We do not make medical claims about our products</li>
                <li>Products are not a substitute for professional medical advice</li>
                <li>Always follow product safety guidelines and age recommendations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">9. Limitation of Liability</h2>
              <p className="leading-relaxed">
                To the fullest extent permitted by law, Samboards shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising from your use of our website or products. Our total liability shall not exceed the amount you paid for the product in question.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">10. Indemnification</h2>
              <p className="leading-relaxed">
                You agree to indemnify and hold Samboards harmless from any claims, damages, losses, or expenses arising from your use of our website, violation of these terms, or infringement of any rights of another party.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">11. Governing Law</h2>
              <p className="leading-relaxed">
                These Terms of Service are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">12. Changes to Terms</h2>
              <p className="leading-relaxed">
                We reserve the right to modify these Terms of Service at any time. Changes will be effective immediately upon posting. Your continued use of the website after changes constitutes acceptance of the modified terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-100 mb-4">13. Contact Information</h2>
              <p className="leading-relaxed">
                For questions about these Terms of Service, please contact us:
              </p>
              <div className="mt-4 p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                <p className="font-medium">Samboards</p>
                <p>Email: samboards19@aol.com</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </Page>
  );
}
